<template>
  <div>

    <el-button v-if="submitButton"
               :disabled="JSON.stringify(values) === JSON.stringify(localValues)"
               type="primary"
               class="filter-btn"
               size="small"
               @click="applyFilter">
      <span>
        <svg-icon :icon-class="'filter'" />
        {{ $t('common.apply') }}
      </span>
    </el-button>

    <div v-for="field in filteredFields" class="field">

      <div v-if="field.type === 'date'" class="field-item">
        <label v-t="field.label" />
        <el-date-picker v-model="localValues[field.key]"
                        :placeholder="field.placeholder && $t(field.placeholder)"
                        :picker-options="field.pickerOptions"
                        :clearable="!!field.clearable"
                        :type="field.dateType ? field.dateType : 'date'"
                        :format="field.format ? field.format : 'yyyy-MM-dd'"
                        size="small"
                        @change="onchange(field.key, field.type)" />
      </div>

      <div v-else-if="field.type === 'dateRange'" class="field-item">
        <label v-t="field.label" />
        <el-date-picker v-model="localValues[field.key]"
                        :start-placeholder="field.startPlaceholder && $t(field.startPlaceholder)"
                        :end-placeholder="field.endPlaceholder && $t(field.endPlaceholder)"
                        :clearable="!!field.clearable"
                        type="daterange"
                        size="small"
                        range-separator="-"
                        @change="onchange(field.key, field.type)" />
      </div>

      <div v-else-if="field.type === 'input'" class="field-item">
        <label v-t="field.label" />
        <el-input v-if="field.debounce"
                  v-model="localValues[field.key]"
                  :clearable="!!field.clearable"
                  :placeholder="field.placeholder && $t(field.placeholder)"
                  :prefix-icon="field.prefixIcon"
                  :class="{ postfix: field.clearable }"
                  size="small"
                  @input="debouncedFunctions[field.key]()" />
        <el-input v-else
                  v-model="localValues[field.key]"
                  :clearable="!!field.clearable"
                  :placeholder="field.placeholder && $t(field.placeholder)"
                  :prefix-icon="field.prefixIcon"
                  :class="{ postfix: field.clearable }"
                  size="small"
                  @change="onchange(field.key)"
                  @keyup.enter="applyFilter" />
      </div>

      <div v-else-if="field.type === 'checkbox'" class="field-item">
        <el-checkbox v-model="localValues[field.key]"
                     @change="onchange(field.key)">
          {{ $t(field.label) }}
        </el-checkbox>
      </div>

      <div v-else-if="field.type === 'checkbox-group'" class="field-item">
        <label v-if="field.label" v-t="field.label" />
        <el-checkbox-group v-model="field.options.model" size="small">
          <el-checkbox-button v-for="item in field.options.list"
                              :key="item.value"
                              :label="$t(item.label)"
                              :value="item.value"
                              :checked="localValues[field.key] && localValues[field.key].includes(item.value)"
                              @change="(checked, event) => onchange(field.key, field.type, {item, checked, event})"
          />
        </el-checkbox-group>
      </div>

      <div v-else-if="field.type === 'radio-group'" class="field-item">
        <label v-if="field.label" v-t="field.label" />
        <el-radio-group v-model="localValues[field.key]" size="small" @change="onchange(field.key)">
          <template v-for="item in field.options.list">
            <el-radio-button :key="item.value" :label="item.value">{{ $t(item.label) }}</el-radio-button>
          </template>
        </el-radio-group>
      </div>

      <div v-else-if="field.type === 'select'" class="field-item">
        <label v-t="field.label" />
        <el-select v-model="localValues[field.key]"
                   :multiple="field.options && field.options.multiple"
                   :placeholder="field.placeholder ? $t(field.placeholder): $t('filter.all')"
                   :filterable="!!field.filterable"
                   :clearable="!!field.clearable"
                   size="small"
                   @change="onchange(field.key)" >
          <el-option v-for="item in field.options.list"
                     :key="item.value"
                     :label="item.label"
                     :value="item.value"/>
        </el-select>
      </div>

      <div v-else-if="field.type === 'user'" class="field-item">
        <label v-t="field.label" />
        <user-select v-model="localValues[field.key]"
                     :role="field.options.role"
                     :placeholder="field.placeholder && $t(field.placeholder)"
                     :popper-append-to-body="false"
                     :clearable="!!field.clearable"
                     class="--dropdown-max-height"
                     @change="onchange(field.key)"
        />
      </div>

      <div v-else-if="field.type === 'port'" class="field-item">
        <label v-t="field.label" />
        <port-select v-model="localValues[field.key]"
                     v-bind="field.options"
                     :multiple="field.options && field.options.multiple"
                     :placeholder="field.placeholder ? $t(field.placeholder): $t('filter.select')"
                     :popper-append-to-body="false"
                     :clearable="!!field.clearable"
                     class="--dropdown-max-height"
                     @change="onchange(field.key)"
        />
      </div>

      <div v-else-if="field.type === 'switch'" class="field-item">
        <label v-t="field.label" />
        <el-switch v-model="localValues[field.key]"
                   v-bind="field.options"
                   @change="onchange(field.key)"
        />
      </div>
    </div>

  </div>
</template>

<script>
// npm
import moment from 'moment'
import {cloneDeep, clone} from 'lodash'
import debounce from 'lodash/debounce'

// other
import PortSelect from '@/components/common/PortSelect'
import {reactiveRemove} from '@/utils/arrays'
import UserSelect from '@/components/common/UserSelect'

export default {
  name: 'FilterComponent',
  components: {UserSelect, PortSelect},
  props: {
    values: {
      type: Object,
      default: () => {}
    },
    fields: {
      type: Array,
      default: () => []
    },
    submitButton: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      localValues: cloneDeep(this.values),
      debouncedFunctions: {}
    }
  },
  computed: {
    filteredFields () {
      let fields = this.fields.filter(f => !f.hidden)
      fields.forEach(field => {
        if (field.type === 'date') {
          if (field.pickerOptions) {
            field.pickerOptions.firstDayOfWeek = 1
          } else {
            field.pickerOptions = {
              firstDayOfWeek: 1
            }
          }
        }
      })
      return fields
    }
  },
  watch: {
    values: {
      handler () {
        this.localValues = cloneDeep(this.values)
      },
      deep: true
    }
  },
  mounted () {
    this.setDebounceFunctions();
  },
  methods: {
    onchange (key, type, data) {
      let localValue = this.localValues[key]
      if (type === 'date') {
        this.localValues[key] = moment(localValue).utc(true).toISOString()
      } else if (type === 'dateRange') {
        localValue && localValue.forEach((value, index) => {
          localValue[index] = moment(value).utc(true).toISOString()
        })
      } else if (type === 'checkbox-group') {
        const {item, checked, event} = data
        !localValue && (localValue = [])
        checked && !localValue.includes(item.value) && localValue.push(item.value)
        !checked && localValue.includes(item.value) && reactiveRemove(localValue, (value) => value === item.value)
        this.debug && console.log('localValue: ', localValue)
      }
      let isImmediate = this.fields.find(field => {
        return (field.key === key && field.immediate)
      })
      let isDebounce = this.fields.find(field => {
        return (field.key === key && field.debounce)
      })
      if (!this.submitButton || isImmediate || isDebounce) {
        this.$set(this.values, key, clone(this.localValues[key]))
        this.$emit('modifiedFilter', this.values, this.values[key], key)
      }

    },
    applyFilter () {
      for (let key in this.localValues) {
        this.$set(this.values, key, clone(this.localValues[key]))
      }
      this.$emit('modifiedFilter', this.values)
    },
    setDebounceFunctions () {
      this.fields.forEach(filterItem => {
        if (filterItem.debounce) {
          this.debouncedFunctions[filterItem.key] = debounce(() => {
            this.onchange(filterItem.key, filterItem.type)
          }, filterItem.debounce)
        }
      })
    }
  }
}
</script>

<style scoped>

  .field-item {
    display: inline-flex;
    flex-wrap: nowrap;
    white-space: nowrap;
    align-items: center;
    margin-right: 15px;
    margin-bottom: 10px;
  }

  .field-item label {
    font-size: 14px;
    color: #606266;
    white-space: nowrap;
  }

  .field-item, .field {
    display: inline-block;
  }

  /deep/ .el-form-item__label {
    display: flex;
    padding: 0 5px 0 15px;
    width: auto !important;
  }

  /deep/ .el-form-item__content {
    margin-left: 0 !important;
  }

  /deep/ .field-item .el-input {
    width: 130px;
  }

  /deep/ .el-date-editor--date {
    width: 130px;
  }

  /deep/ .el-date-editor--daterange {
    width: 220px;
  }

  /deep/ .el-select__tags > span {
    display: inline-flex;
    overflow: hidden;
  }

  /deep/ .el-select .el-input__inner {
    height: 32px !important;
  }

  >>> .el-input__inner {
    padding-right: 15px !important;
  }
  >>> .postfix .el-input__inner {
    padding-right: 30px;
  }

  .no-tabled-form .el-form-item::after, .no-tabled-form .el-form-item::before {
    display: table;
    content: "";
  }

  .filter-btn {
    float: right;
    height: 32px;
  }

  .filter-btn span {
    display: flex;
  }

  .filter-btn span svg {
    width: 1.3em;
    height: 1.3em;
    margin-right: 5px;
  }
</style>



// WEBPACK FOOTER //
// src/components/common/FilterComponent.vue